import { useCallback, useEffect, useRef, useState } from 'react';
import useAuth from 'hooks/useAuth';
import { useTenantClient } from 'client/client-hook';

/** SeatTier enum values — must match the proto (STANDARD=1, PREMIUM=2, DEDICATED=3, FUTURES=4). */
const TIER = { STANDARD: 1, PREMIUM: 2, DEDICATED: 3, FUTURES: 4 } as const;

export type SeatPool = { capacity: number; assigned: number };
export type RoomSeats = { standard: SeatPool; premium: SeatPool; futures: SeatPool; dedicated: SeatPool };

export type RoomListItem = {
  tenantId: string;
  name: string;
  logoUrl: string;
  active: boolean;
  members: number;
  accounts: number;
  accountLimit: number;
  seats: RoomSeats;
  /** The room's own Stripe subscription; '' when the backend hasn't set one. */
  subscriptionId: string;
};

const EMPTY_POOL: SeatPool = { capacity: 0, assigned: 0 };
const EMPTY_SEATS: RoomSeats = { standard: EMPTY_POOL, premium: EMPTY_POOL, futures: EMPTY_POOL, dedicated: EMPTY_POOL };

// Lists the rooms the current user owns. The list RPC returns zeroed counts, so we fan
// out GetMyTenant + GetSeatUsage per room to enrich each card with live stats.
export function useMyRooms() {
  const getTenantClient = useTenantClient();
  // The client getter changes identity whenever the access token is refreshed — keep it in a ref, out of deps.
  const getClientRef = useRef(getTenantClient);
  getClientRef.current = getTenantClient;
  const { user } = useAuth() as any;
  const tcId = user?.tcId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rooms, setRooms] = useState<RoomListItem[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const client = await getClientRef.current();
        const res = await client.getMyTenants();
        const owned = (res?.tenantsList ?? []).filter((t: any) => !tcId || t.ownerUserId === tcId);

        const detailed = await Promise.all(
          owned.map(async (t: any): Promise<RoomListItem> => {
            const base: RoomListItem = {
              tenantId: t.tenantId,
              name: t.name || t.tenantId,
              logoUrl: '',
              active: t.active ?? true,
              members: 0,
              accounts: 0,
              accountLimit: t.accountLimit ?? 0,
              seats: EMPTY_SEATS,
              subscriptionId: t.subscriptionId ?? ''
            };
            try {
              const [d, usage] = await Promise.all([client.getMyTenant(t.tenantId), client.getSeatUsage(t.tenantId).catch(() => null)]);
              const info = d?.tenant ?? {};
              const b = d?.branding ?? {};
              const pick = (tier: number): SeatPool => {
                const row = (usage?.tiersList ?? []).find((x: any) => x.tier === tier);
                return { capacity: row?.capacity ?? 0, assigned: row?.assigned ?? 0 };
              };
              return {
                ...base,
                name: b.roomName || info.name || base.name,
                logoUrl: b.logoUrl || '',
                active: info.active ?? base.active,
                members: info.activeUsers ?? 0,
                accounts: info.currentAccounts ?? 0,
                accountLimit: info.accountLimit ?? base.accountLimit,
                subscriptionId: info.subscriptionId || base.subscriptionId,
                seats: {
                  standard: pick(TIER.STANDARD),
                  premium: pick(TIER.PREMIUM),
                  futures: pick(TIER.FUTURES),
                  dedicated: pick(TIER.DEDICATED)
                }
              };
            } catch {
              return base; // one room's detail failing shouldn't drop it from the list
            }
          })
        );
        if (!cancelled) setRooms(detailed);
      } catch (e: any) {
        // PermissionDenied (7): the async Room Owner role grant isn't in the token yet.
        if (!cancelled) setError(e?.code === 7 ? 'PROVISIONING' : e?.message || 'Could not load your rooms.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tcId, reloadKey]);

  return { loading, error, rooms, reload: useCallback(() => setReloadKey((n) => n + 1), []) };
}
